'use client';

import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';

import { ProductCompareContext, ProductCompareContextValue, ProductCompareItem } from '../context/product-compare-context';

const STORAGE_KEY = 'productCompareItems';
const MAX_PRODUCTS = 5;

/**
 * Normalize product ID to ensure 'product-' prefix without duplication
 */
const normalizeProductId = (productId: string): string => (productId.startsWith('product-') ? productId : `product-${productId}`);

const getProductItemsFromStorage = (): ProductCompareItem[] => {
   if (typeof window === 'undefined') return [];

   try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return [];

      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
   } catch (error) {
      console.error('Error reading product compare items from localStorage:', error);
      return [];
   }
};

const saveProductItemsToStorage = (items: ProductCompareItem[]): void => {
   if (typeof window === 'undefined') return;

   try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
   } catch (error) {
      console.error('Error saving productsToCompare to localStorage:', error);
   }
};

interface ProductCompareProviderProps extends PropsWithChildren {
   languageCode: string;
}

export const ProductCompareProvider = ({ children, languageCode: _languageCode }: ProductCompareProviderProps) => {
   const [productItems, setProductItems] = useState<ProductCompareItem[]>([]);
   const [isInitialized, setIsInitialized] = useState(false);

   // Sync productItems from localStorage on mount (after hydration)
   useEffect(() => {
      const storedItems = getProductItemsFromStorage();
      setProductItems(storedItems);
      setIsInitialized(true);
   }, []);

   const addProduct = useCallback((productId: string, productName: string) => {
      const normalizedId = normalizeProductId(productId);

      setProductItems((prevItems) => {
         if (prevItems.some((item) => item.id === normalizedId) || prevItems.length >= MAX_PRODUCTS) {
            return prevItems;
         }

         const newItems = [...prevItems, { id: normalizedId, name: productName }];
         saveProductItemsToStorage(newItems);
         return newItems;
      });
   }, []);

   const removeProduct = useCallback((productId: string) => {
      const normalizedId = normalizeProductId(productId);

      setProductItems((prevItems) => {
         const newItems = prevItems.filter((item) => item.id !== normalizedId);
         saveProductItemsToStorage(newItems);
         return newItems;
      });
   }, []);

   const removeAllProducts = useCallback(() => {
      setProductItems([]);
      saveProductItemsToStorage([]);
   }, []);

   const contextValue: ProductCompareContextValue = useMemo(
      () => ({
         productItems,
         isInitialized,
         addProduct,
         removeProduct,
         removeAllProducts
      }),
      [productItems, isInitialized, addProduct, removeProduct, removeAllProducts]
   );

   return <ProductCompareContext value={contextValue}>{children}</ProductCompareContext>;
};
